/**
 * Hook for loading the patient's Blueprint card graph (anchor + emergent cards).
 *
 * The Blueprint is now a graph of ``type=entity`` cards (anchor cards for the
 * six canonical sections, plus emergent cards for people, challenges, goals,
 * interventions, etc.). This hook fetches the wiki index (grouped by kind) and
 * lazily loads individual card content on demand, providing the data layer for
 * {@link BlueprintTab}'s card-native rendering.
 *
 * Endpoint map:
 *   GET /api/wiki/index?patient_id=   → catalogue of all cards (WikiIndex)
 *   GET /api/entities/{document_id}   → full card content (EntityPage)
 *
 * All clinical content stays in component state; nothing is logged (HIPAA).
 */
import { useCallback, useEffect, useState } from "react";
import apiUtils, { isApiError } from "../apiUtils";
import logger from "../utils/logger";
import type { EntityPage, WikiIndex, WikiIndexEntry } from "../types";

/** The lifecycle status of the blueprint card index. */
export type CardStatus = "loading" | "loaded" | "empty" | "error";

/** Anchor card kinds (the six reserved scaffold cards). */
const ANCHOR_KINDS = new Set(["section", "overview"]);

/** A grouped view of the card catalogue: anchor cards first, then by kind. */
export interface CardGroups {
  /** The six reserved scaffold cards (section/overview kind), in index order. */
  anchors: WikiIndexEntry[];
  /** Remaining cards, keyed by kind (alphabetically sorted). */
  byKind: Map<string, WikiIndexEntry[]>;
}

/** The surface returned by {@link useBlueprintCards}. */
export interface UseBlueprintCardsResult {
  /** The current load status of the card index. */
  status: CardStatus;
  /** The grouped card catalogue. */
  groups: CardGroups;
  /** Load a single card's full content by its entity document id. */
  loadCard: (documentId: string) => Promise<EntityPage | null>;
  /** Re-fetch the card index. */
  reload: () => void;
}

/**
 * Group wiki index entries into anchor cards and emergent cards by kind.
 *
 * @param pages - The flat list of index entries.
 * @returns Grouped entries: anchors (section/overview) then by kind.
 */
function groupCards(pages: WikiIndexEntry[]): CardGroups {
  const anchors: WikiIndexEntry[] = [];
  const byKind = new Map<string, WikiIndexEntry[]>();

  for (const entry of pages) {
    if (ANCHOR_KINDS.has(entry.kind)) {
      anchors.push(entry);
    } else {
      const existing = byKind.get(entry.kind);
      if (existing) {
        existing.push(entry);
      } else {
        byKind.set(entry.kind, [entry]);
      }
    }
  }

  // Sort byKind map by key for stable rendering.
  const sorted = new Map(
    [...byKind.entries()].sort(([a], [b]) => a.localeCompare(b)),
  );

  return { anchors, byKind: sorted };
}

/**
 * Normalize a raw wiki index response into a safe {@link WikiIndex}.
 *
 * @param raw - The raw GET /api/wiki/index response.
 * @returns A normalized index with a guaranteed pages array.
 */
function normalizeIndex(raw: unknown): WikiIndex {
  if (typeof raw !== "object" || raw === null) {
    return { patient_id: "", pages: [] };
  }
  const obj = raw as Record<string, unknown>;
  const groups =
    typeof obj.groups === "object" && obj.groups !== null
      ? (obj.groups as Record<string, unknown>)
      : {};
  const pages = Object.values(groups).flatMap((entries) =>
    Array.isArray(entries) ? (entries as WikiIndexEntry[]) : [],
  );
  return {
    patient_id: typeof obj.patient_id === "string" ? obj.patient_id : "",
    pages,
  };
}

const WIKI_INDEX = "/api/wiki/index";
const ENTITIES = "/api/entities";

/**
 * Load and group the Blueprint card graph for a patient.
 *
 * Fetches the wiki index and groups entries into anchor cards (the six
 * canonical section scaffolds) and emergent cards (by kind). Individual
 * card content is loaded lazily on demand via {@link loadCard}.
 *
 * @param patientId - The active patient id.
 * @returns The card status, grouped catalogue, loadCard action, and reload.
 */
export function useBlueprintCards(patientId: string): UseBlueprintCardsResult {
  const [status, setStatus] = useState<CardStatus>("loading");
  const [groups, setGroups] = useState<CardGroups>({
    anchors: [],
    byKind: new Map(),
  });
  const [token, setToken] = useState(0);

  const reload = useCallback(() => setToken((t) => t + 1), []);

  useEffect(() => {
    let active = true;

    async function load(): Promise<void> {
      setStatus("loading");
      const url = `${WIKI_INDEX}?patient_id=${encodeURIComponent(patientId)}`;
      const resp = await apiUtils.fetchData<unknown>(url);
      if (!active) return;
      if (isApiError(resp)) {
        setStatus("error");
        return;
      }
      const index = normalizeIndex(resp);
      const grouped = groupCards(index.pages);
      setGroups(grouped);
      setStatus(index.pages.length === 0 ? "empty" : "loaded");
    }

    load().catch((err: unknown) => {
      if (!active) return;
      logger.devOnly.warn("useBlueprintCards: load failed", {
        error: String(err),
      });
      setStatus("error");
    });

    return () => {
      active = false;
    };
  }, [patientId, token]);

  const loadCard = useCallback(
    async (documentId: string): Promise<EntityPage | null> => {
      const resp = await apiUtils.fetchData<EntityPage>(
        `${ENTITIES}/${encodeURIComponent(documentId)}`,
      );
      if (isApiError(resp)) {
        logger.devOnly.warn("useBlueprintCards: loadCard failed");
        return null;
      }
      return resp;
    },
    [],
  );

  return { status, groups, loadCard, reload };
}
